import { BrowserModule } from '@angular/platform-browser';
import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ToastrModule } from 'ngx-toastr';
import { ModalModule } from 'ngx-bootstrap/modal';
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { AlertModule } from 'ngx-bootstrap/alert';
import { AppComponent } from './app.component';
import { RouterModule } from '@angular/router';

import { BaseComponent } from 'web-common/src/app/base/base.component';
import { HeaderModule } from 'web-common/src/app/header/header.module';
import { ErrorInterceptor } from 'web-common/src/app/interceptor/error.interceptor';
import { CookieService } from 'web-common/src/app/cookie.service';
import { WindowService } from 'web-common/src/app/window.service';
import { I18nService } from 'web-common/src/app/i18n.service';
import { SidebarModule } from 'web-common/src/app/sidebar/sidebar.module';
import { NotificationsService } from 'web-common/src/app/notifications.service';
import { userResolver } from 'web-common/src/app/user/user.service';
import { SettingsService } from './services/settings.service';
import { InviteService } from './services/inviteusers.service';
import { AdminUsersService } from './admin-users/admin-users.service';

import { AdminUsersComponent } from './admin-users/admin-users.component';
import { AdminSidebarComponent } from './admin-sidebar/admin-sidebar.component';
import { AdminSettingsComponent } from './admin-settings/admin-settings.component';
import { FilterStylesPipe } from './admin-settings/admin-settings.styles.pipe';
import { AdminAnalyticsComponent } from './admin-analytics/admin-analytics.component';
import { AdminInviteComponent } from './admin-invite/admin-invite.component';
import { AdminInviteSidebarComponent } from './admin-invite-sidebar/admin-invite-sidebar.component';
import { UserStatusesService } from './user-statuses.service';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { AdminUserGuard, canActivateAdminUser } from './admin-user.guard';
import { AdminNonSubscriberComponent } from './admin-non-subscriber/admin-non-subscriber.component';
import { AdminAnalyticsService } from 'app/admin-analytics/admin-analytics.service';

import { GoogleChart, provideGoogleCharts } from 'angular-google-charts';
import { AdminModalFactoryComponent} from './admin-users/modal-factory/admin-modal-factory.component';
import { ConfirmMakeUserAdminComponent } from './admin-users/modal-factory/confirm-make-user-admin/confirm-make-user-admin.component';
import { ConfirmRemoveUserAdminComponent } from './admin-users/modal-factory/confirm-remove-user-admin/confirm-remove-user-admin.component';
import { ConfirmDeactivateUserComponent } from './admin-users/modal-factory/confirm-deactivate-user/confirm-deactivate-user.component';
import { ConfirmReactivateUserComponent } from './admin-users/modal-factory/confirm-reactivate-user/confirm-reactivate-user.component';
import { ExampleModalComponent } from './admin-settings/admin-settings-modal/profile-information-modal/example-modal.component';
import { FindSampleModalComponent } from './admin-settings/admin-settings-modal/find-options-modal/find-sample-modal.component';
import { AdminCreationComponent } from './admin-creation/admin-creation.component';
import {AccountCreationService} from './services/account-creation.service';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi, withJsonpSupport } from '@angular/common/http';
import {RolesModalComponent} from './admin-creation/admin-creation-modal/roles-modal/roles-modal.component';
import {AoFModalComponent} from './admin-creation/admin-creation-modal/aof-modal/aof-modal.component';
import {TrapFocusDirective} from './directives/trap-focus-directive';
import { InvitedComponent } from './invited/invited.component';
import { MatNativeDateModule } from '@angular/material/core';
import { MatInputModule } from '@angular/material/input';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { A11yModule } from '@angular/cdk/a11y';
import { ReportDownloadNotificationComponent } from './admin-users/modal-factory/report-download-notification/report-download-notification.component';
import { SeatLimitReachedComponent } from './admin-users/modal-factory/seat-limit-reached/seat-limit-reached.component';

declare var require: any;

@NgModule({ declarations: [
        AppComponent,
        AdminUsersComponent,
        AdminSidebarComponent,
        AdminSettingsComponent,
        AdminAnalyticsComponent,
        AdminInviteComponent,
        AdminInviteSidebarComponent,
        AdminNonSubscriberComponent,
        FilterStylesPipe,
        AdminModalFactoryComponent,
        ConfirmMakeUserAdminComponent,
        ConfirmRemoveUserAdminComponent,
        ConfirmDeactivateUserComponent,
        ConfirmReactivateUserComponent,
        ExampleModalComponent,
        FindSampleModalComponent,
        AdminCreationComponent,
        RolesModalComponent,
        AoFModalComponent,
        TrapFocusDirective,
        InvitedComponent,
        ReportDownloadNotificationComponent,
        SeatLimitReachedComponent
    ],
    bootstrap: [
        AppComponent
    ], imports: [BrowserModule,
        BrowserAnimationsModule,
        FormsModule,
        HeaderModule.forRoot({
            showProjectsLink: true
        }),
        SidebarModule,
        MatNativeDateModule,
        MatInputModule,
        MatDatepickerModule,
        TooltipModule,
        ReactiveFormsModule,
        A11yModule,
        GoogleChart,
        AlertModule.forRoot(),
        BsDropdownModule.forRoot(),
        ToastrModule.forRoot(),
        ModalModule.forRoot(),
        RouterModule.forRoot([
            {
                path: '',
                component: BaseComponent,
                resolve: {
                    user: userResolver
                },
                children: [
                    {
                        path: '',
                        redirectTo: 'users',
                        pathMatch: 'full'
                    }, {
                        path: 'users',
                        component: AdminUsersComponent,
                        canActivate: [canActivateAdminUser],
                        resolve: {
                            user: userResolver
                        }
                    }, {
                        path: 'users/invite',
                        component: AdminInviteComponent,
                        canActivate: [canActivateAdminUser]
                    }, {
                        path: 'settings',
                        component: AdminSettingsComponent,
                        canActivate: [canActivateAdminUser]
                    }, {
                        path: 'account-creation',
                        component: AdminCreationComponent,
                        canActivate: [canActivateAdminUser]
                    }, {
                        path: 'analytics',
                        redirectTo: 'analytics/usage',
                        pathMatch: 'full'
                    }, {
                        path: 'analytics/usage',
                        component: AdminAnalyticsComponent,
                        canActivate: [canActivateAdminUser],
                        data: { title: 'Usage Analytics', eventAction: null }
                    },{
                        path: 'analytics/accounts-created',
                        component: AdminAnalyticsComponent,
                        canActivate: [canActivateAdminUser],
                        data: { title: 'Accounts Created', eventAction: 'accounts' }
                    },{
                        path: 'analytics/references-added',
                        component: AdminAnalyticsComponent,
                        canActivate: [canActivateAdminUser],
                        data: { title: 'References Added', eventAction: 'refs' }
                    }, {
                        path: 'analytics/publications-added',
                        component: AdminAnalyticsComponent,
                        canActivate: [canActivateAdminUser],
                        data: { title: 'Publications Added', eventAction: 'add' }
                    }, {
                        path: 'analytics/publications-cited',
                        component: AdminAnalyticsComponent,
                        canActivate: [canActivateAdminUser],
                        data: { title: 'Publications Cited', eventAction: 'cite' }
                    }, {
                        path: 'analytics/publications-read',
                        component: AdminAnalyticsComponent,
                        canActivate: [canActivateAdminUser],
                        data: { title: 'Publications Read', eventAction: 'read' }
                    }, {
                        path: 'analytics/publications-viewed',
                        component: AdminAnalyticsComponent,
                        canActivate: [canActivateAdminUser],
                        data: { title: 'Publications Viewed', eventAction: 'view' }
                    }
                ]
            }
        ], { useHash: true })], providers: [
        { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },
        CookieService,
        WindowService,
        I18nService,
        UserStatusesService,
        NotificationsService,
        SettingsService,
        InviteService,
        AdminUsersService,
        AdminUserGuard,
        AdminAnalyticsService,
        AccountCreationService,
        provideHttpClient(withInterceptorsFromDi(), withJsonpSupport()),
        provideGoogleCharts()
    ] })
export class AppModule { }
